import { Alert, Button, ColumnLayout, Flash, Spinner } from 'AWS-UI-Components-React';
import React from 'react';
import { CHANNELS } from '../../constants/channels';
import { GOAL_KEY } from '../../constants/trend_report';
import { REPORT_TYPE } from '../../constants/report_type';
import { AccuracyButtonNewStyle } from '../../styles/common_styles';
import { downloadFile } from '../../utilities';
import { CustomizedDot, getMaximallyDifferentRandomColor } from '../../utils/graph-utils';
import logger from '../../utils/logging';
import { TrendFilter } from '../buttons/Trend_Filter';
import { getJSClient } from '../js_client';

const Recharts = window.Recharts;

const LineChart = Recharts.LineChart;
const XAxis = Recharts.XAxis;
const YAxis = Recharts.YAxis;
const CartesianGrid = Recharts.CartesianGrid;
const Tooltip = Recharts.Tooltip;
const Legend = Recharts.Legend;
const Line = Recharts.Line;
const ResponsiveContainer = Recharts.ResponsiveContainer;
const BarChart = Recharts.BarChart;
const ComposedChart = Recharts.ComposedChart;
const Bar = Recharts.Bar;


export class AccuracyTrend extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            shownSummaryMetrics: [],
            shownSummaryChannels: [],
            loadDone: false,
            loadError: false,
            shownComponentAccuracyKeys: [],
            shownComponentChannels: [],
            shownComponents: [],
            shownSummarySnapshots: [],
            shownComponentSnapshots: [],
            emptyRecords: false,
            reportDownloading: false,
            showBarChartComponent: false,
            showBarChartSummary: false,
            queryExecutionId: null,
            queryDataScanned: null,
            numberOfCall: 2,
            errorState: 'Unknown Error',
        };
        this.trendGoals = {};
        this.goalLineSummaryColor = 0;
        this.goalLineComponentColor = 0;
        this.processedSummaryData = [];
        this.processedComponentData = [];
        this.loadedAccuracyData = [];
        this.completedQueries = 0;
        this.allChannels = [];
        this.allComponents = [];
        this.loadedSnapshotIndex = 0;
        this.totalMonths = this.props.payload.timeFrame.split(",").map(time => time.includes("/")? 1 : time.includes("Q") ? 3 : 12)
                                                                  .reduce((a, b) => {return a + b;}, 0);
    }

    componentDidMount = () => {
        this.client = getJSClient();
        this.initialClientTasks();
    };

    componentDidUpdate = (prevProps) => {
        if (prevProps.downloadData !== this.props.downloadData && this.props.downloadData) {
            this.onDownloadData();
        }
    };

    initialClientTasks = () => {
        this.client.getChannelTrendGoalConfig(this.onChannelTrendGoalsConfigFetchSuccess);
        this.client.getComponentTrendGoalConfig(this.onComponentTrendGoalsConfigFetchSuccess);
        this.setState({loadStartTimestamp: performance.now()});
        for (let i = 0; i < this.props.snapshots.length; i++) {
            this.pullDataWithRetryAndTimeout(i, 2);
        }
    };


    onChannelTrendGoalsConfigFetchSuccess = (response) => {
        this.storeTrendGoals(response.channelTrendGoalConfig);
    };

    onComponentTrendGoalsConfigFetchSuccess = (response) => {
        this.storeTrendGoals(response.componentTrendGoalConfig);
    };

    storeTrendGoals = (config) => {
        try {
            for (const goal of JSON.parse(config)) {
                this.trendGoals[goal.id] = goal.trendGoal;
            }
        } catch (e) {
            logger.error('Unable to fetch trend goals', e);
        }
    };

    pullDataWithRetryAndTimeout = (index, retry) => {
        if (retry > 0) {
            return this.pullData(index, retry)
                       .then(() => this.sleep(5000))
                       .then(() => {retry = this.checkDone(retry);})
                       .then(() => this.pullDataWithRetryAndTimeout(index, retry-1));
        }
    };

    checkDone = (retry) => {
        if (this.state.loadDone) {
            retry--;
        }
        return retry;
    };

    pullData = async (index, retry) => {
        this.client.pullAccuracyData(
            this.onAccuracyPull,
            this.onAccuracyFailure,
            this.props.payload,
            this.state.queryExecutionId,
            this.props.snapshots,
            index,
            null,
            retry
        );
    };

    sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

    onAccuracyFailure = (error, retry) => {
        // Ignore failure for first call
        if (retry !== this.state.numberOfCall) {
            logger.error('Load trend report failure', error, this.props.payload);
            this.setState({loadError: true, loadDone: true});
        }
    };

    onAccuracyPull = async (response, snapshotIndex, payload, item, retry) => {
        console.log(
            'Received response from snapshot %s: %s,%s,%s ',
            this.props.snapshots[snapshotIndex].id,
            response.queryExecutionStatus,
            response.queryExecutionId,
            response.queryDataScanned
        );

        if (response.queryExecutionStatus === 'INCOMPLETE') {
            this.setState({ queryExecutionId: response.queryExecutionId });
            await this.sleep(2000);
            if (!this.state.loadDone) {
                console.log('Resend request: ' + this.state.queryExecutionId);
                this.pullData(snapshotIndex, retry);
            }
        } else if (response.queryExecutionStatus === 'COMPLETE'){
            if (this.state.loadDone && !this.state.loadError) {
                return;
            }
            console.log(
                'Accuracy Data Pulled from %s (%d of %d)',
                this.props.snapshots[snapshotIndex].id,
                ++this.completedQueries,
                this.props.snapshots.length
            );
            this.loadedAccuracyData[snapshotIndex] = {
                sortedKeys: response.sortedKeys,
                sortedHeadings: response.sortedHeadings,
                accuracyTrend: response.accuracyTrend,
                summaryTrend: response.summaryTrend,
                sortedMonths: response.sortedMonths
            };
        } else {
            console.log('report failed with ' + response.queryExecutionStatus + ': ' + response.reportStatus);
            logger.error(`Accuracy Data pull exception: ${response.queryExecutionStatus}`,
                         {error: response.reportStatus, payload: this.props.payload});
            this.setState({loadError: true, loadDone: true, errorState: response.queryExecutionStatus});
        }
        this.checkAllSnapshotLoaded();
    };

    checkAllSnapshotLoaded = () => {
        while (
            this.loadedSnapshotIndex < this.props.snapshots.length &&
            this.loadedSnapshotIndex < this.loadedAccuracyData.length &&
            this.loadedAccuracyData[this.loadedSnapshotIndex]
        ) {
            this.loadedSnapshotIndex = this.loadedSnapshotIndex + 1;
        }
        if(this.loadedSnapshotIndex === this.props.snapshots.length) {
            this.processData();
        }
    };

    processHeadings = () => {
        let commonHeadings = ["Region", "Channel", "Marketplace", "Month", "Component", "Component Type"];
        let sortedHeadings = this.loadedAccuracyData[0].sortedHeadings;
        let sortedCommonHeadings = sortedHeadings.filter(heading => commonHeadings.includes(heading));
        let headings = [].concat(sortedCommonHeadings);
        for(let i = sortedCommonHeadings.length; i < sortedHeadings.length; i++) {
            const snapshots = this.props.snapshots.map(snapshot => {
                return snapshot.id === this.props.appConfig.liveSnapshotId ?
                    `[live]${sortedHeadings[i]}`:
                    `[${snapshot.name}]${sortedHeadings[i]}`
            });
            headings.push(...snapshots);
        }
        return headings;
    };

    onDownloadData = () => {
        let array = [];
        let headings = this.props.snapshots.length === 1? this.loadedAccuracyData[0].sortedHeadings:this.processHeadings();
        array.push(headings.join('\t'));
        let keys = this.loadedAccuracyData[0].sortedKeys;
        let commonKeys = keys.filter(key => ["region", "channel", "marketplace", "month", "component", "type"].includes(key));
        let dataSortedByCountOfMonths = this.loadedAccuracyData.sort((a, b) => b.sortedMonths.length - a.sortedMonths.length);
        for(let monthIndex = 0; monthIndex < dataSortedByCountOfMonths[0].sortedMonths.length; monthIndex++) {
            for(let accuracyRecordIndex = 0; accuracyRecordIndex < dataSortedByCountOfMonths[0].accuracyTrend[monthIndex].length; accuracyRecordIndex++) {
                let rowArray = commonKeys.map(key => dataSortedByCountOfMonths[0].accuracyTrend[monthIndex][accuracyRecordIndex][key]);
                let month = dataSortedByCountOfMonths[0].accuracyTrend[monthIndex][accuracyRecordIndex].month;
                keys.filter(recordKey => !commonKeys.includes(recordKey))
                    .map(recordKey => {rowArray.push(
                        ...dataSortedByCountOfMonths.map(accuracyData => {
                            if(accuracyData.sortedMonths.includes(month)) {
                                let monthIndexForCurrentSnapshot = accuracyData.sortedMonths.indexOf(month);
                                return accuracyData.accuracyTrend[monthIndexForCurrentSnapshot][accuracyRecordIndex][recordKey]
                            } else {
                                return ""
                            }
                        }))});
                array.push(rowArray.join('\t'));
            }
        }
        downloadFile(array.join('\n'), 'Accuracy Trend Data.xls');
        this.props.resetButtons();
    };

    setDefaultData= () => {
        let retailOrFirstSelectedChannel = this.allChannels.includes(CHANNELS.RETAIL)?CHANNELS.RETAIL:this.allChannels[0];
        let defaultIsBarChart = this.defaultIsBarChart();
        this.setState({shownSummaryChannels: [retailOrFirstSelectedChannel],
                             shownSummaryMetrics: ["lastCpAccuracy-cpAccuracy", "firstCpAvAccuracy-cpAccuracy"],
                             shownComponentChannels: [retailOrFirstSelectedChannel],
                             shownComponentAccuracyKeys: ["dailyCPAccuracyAV", "lastCPAccuracyNV"],
                             shownSummarySnapshots: [...Array(this.loadedAccuracyData.length).keys()],
                             shownComponentSnapshots: [...Array(this.loadedAccuracyData.length).keys()],
                             showBarChartComponent: defaultIsBarChart,
                             showBarChartSummary: defaultIsBarChart});

    };

    defaultIsBarChart = () => {
        let sortedMonths = this.loadedAccuracyData.map(data => data.sortedMonths);
        let sharedMonth = sortedMonths.shift().filter(month => {
            return sortedMonths.every(sortedMonth => {
                return sortedMonth.indexOf(month) !== -1;
            })
        });
        if(sharedMonth.length < 2) {
            return true;
        }
        for(let i = 0; i <sharedMonth.length - 1; i++) {
            if(sharedMonth[i].includes("Q")) {
                return true;
            }
            let nextMonthInList = new Date(sharedMonth[i + 1] / 100, sharedMonth[i + 1] % 100 - 1);
            let nextContinuousMonth = new Date(sharedMonth[i] / 100, sharedMonth[i] % 100);
            if(nextMonthInList.getTime() !== nextContinuousMonth.getTime()) {
                return true;
            }
        }
        return false;
    };

    processData = () => {
        this.setState({loadEndTimestamp: performance.now()});
        logger.info('Report response time', {
            responseTime: (this.state.loadEndTimestamp - this.state.loadStartTimestamp), report: 'trend'});
        const reportType = REPORT_TYPE.TREND;
        if(this.loadedAccuracyData.every(snapshotData => snapshotData.sortedMonths.length)) {
            this.processedSummaryData = this.processSummaryTrend();
            this.processedComponentData = this.processComponentTrend();
            this.setDefaultData();
            this.setState({ loadDone: true});
            this.props.onLoadDone({ reportType });
        } else {
            this.setState({emptyRecords: true,
                                 loadDone: true})
        }
    };

    processComponentTrend = () => {
        let processedComponentData = {};
        for(let snapshotIndex = 0; snapshotIndex < this.loadedAccuracyData.length; snapshotIndex++) {
            let accuracyTrend = this.loadedAccuracyData[snapshotIndex].accuracyTrend;
            let sortedMonth = this.loadedAccuracyData[snapshotIndex].sortedMonths;
            if (accuracyTrend.length !== sortedMonth.length) {
                console.log("Response data not match");
                this.setState({ loadError: true, loadDone: true });
            }
            accuracyTrend.map(monthlyAccuracy => {
                let month = monthlyAccuracy[0].month;
                if(!processedComponentData.hasOwnProperty(month)) {
                    processedComponentData[month] = {
                        formattedMonth : this.formatDate(month),
                        month: month
                    };
                }
                monthlyAccuracy.forEach((componentAccuracyRecord) => {
                    let channelName = componentAccuracyRecord.channel;
                    if(snapshotIndex === 0 && !this.allChannels.includes(channelName)) {
                        this.allChannels.push(channelName);
                    }
                    let componentName = componentAccuracyRecord.component;
                    if(snapshotIndex === 0 && !this.allComponents.includes(componentName)) {
                        this.allComponents.push(componentName);
                    }
                    Object.keys(componentAccuracyRecord).map((recordKey) => {
                        if(this.componentGraphAccuracyOptionKeys.includes(recordKey)) {
                            let componentId = [channelName, recordKey, componentName, snapshotIndex].join("-");
                            processedComponentData[month][componentId] = this.reformatRecordData(componentAccuracyRecord[recordKey]);
                            if (recordKey.includes('dailyCPAccuracyAV')) {
                                let key = [channelName, recordKey, componentName, snapshotIndex, GOAL_KEY].join('-');
                                processedComponentData[month][key] = this.reformatRecordData(this.getTrendGoal(componentName.replace(/\s/g, '')));
                            }
                            if (componentName === "Contribution Profit" && recordKey === "perUnitNetVarianceLastCP") {
                                let key = `${channelName}-perUnitVariance- -${snapshotIndex}`;
                                this.processedSummaryData[month][key] = this.reformatRecordData(componentAccuracyRecord[recordKey]);
                            }
                        }
                    });
                });
            })
        }
        return processedComponentData;
    };

    componentGraphAccuracyOptionKeys = ["dailyCPAccuracyNV", "lastCPAccuracyNV", "dailyCPAccuracyAV", "lastCPAccuracyAV",
        "perUnitNetVarianceDailyCP", "perUnitNetVarianceLastCP", "perUnitVarianceDailyCP", "perUnitVarianceLastCP"];

    getSortedKeySortedHeadingMap = () => {
        let keys = this.loadedAccuracyData[0].sortedKeys;
        let headings = this.loadedAccuracyData[0].sortedHeadings;
        let map = {};
        keys.forEach((key, i) => map[key] = headings[i]);
        return map;
    };

    getTrendGoal =(id) => {
        for (const [key, goal] of Object.entries(this.trendGoals)) {
           if (key.includes(id)) {
               return goal;
           }
        }
        return 0;
    };

    processSummaryTrend = () => {
        let flattenedTrendWithMonth = {};
        for(let snapshotIndex = 0; snapshotIndex < this.loadedAccuracyData.length; snapshotIndex++) {
            let summaryTrend = this.loadedAccuracyData[snapshotIndex].summaryTrend;
            let sortedMonth = this.loadedAccuracyData[snapshotIndex].sortedMonths;
            if (summaryTrend.length !== sortedMonth.length) {
                console.log("Response data not match");
                this.setState({ loadError: true, loadDone: true });
            }

            for(let monthIndex = 0; monthIndex  < summaryTrend.length; monthIndex ++) {
                let month = sortedMonth[monthIndex];
                if(!flattenedTrendWithMonth.hasOwnProperty(month)) {
                    flattenedTrendWithMonth[month] = {
                        formattedMonth: this.formatDate(month),
                        month: month
                    };
                }
                let monthlySummaryTrend = summaryTrend[monthIndex];
                Object.keys(monthlySummaryTrend).map((channelKey) => {
                    Object.keys(monthlySummaryTrend[channelKey]).map((answerType) => {
                        Object.keys(monthlySummaryTrend[channelKey][answerType]).map((accuracyType) => {
                            let channel = channelKey.replace("ChannelSummaryAccuracy", "").toUpperCase();
                            let accuracyTypeKey = [channel, answerType, accuracyType, snapshotIndex].join('-');
                            flattenedTrendWithMonth[month][accuracyTypeKey] = this.reformatRecordData(monthlySummaryTrend[channelKey][answerType][accuracyType]);
                            if (answerType.includes('firstCpAvAccuracy')) {
                                let accuracyTypeKey = [channel, answerType, accuracyType, snapshotIndex, GOAL_KEY].join('-');
                                flattenedTrendWithMonth[month][accuracyTypeKey] = this.reformatRecordData(this.getTrendGoal(channel));
                            }
                        });
                    });
                });
            }
        }
        return flattenedTrendWithMonth;
    };

    formatDate = (time) => {
        if(time.includes("Q")) {
            return [time.substring(4, 6), time.substring(0, 4)].join(',');
        }
        return [this.props.monthMap[time.substring(4, 6)], time.substring(0, 4)].join(',');
    };

    reformatRecordData = (data) => {
        return parseFloat(data.toFixed(2));
    };

    accuracyTypeIdLabelMap = {
        cpAccuracy: "Accuracy",
        transactionAccuracy: "Transactional Accuracy",
        standardRateAccuracy: "Standard Rate Accuracy",
        predictiveAccuracy: "Predictive Accuracy",
    };

    answerTypeIdLabelMap = {
        firstCpAccuracy: "First Answer",
        lastCpAccuracy: "Last Answer",
        firstCpAvAccuracy: "First Answer(Abs)",
        lastCpAvAccuracy: "Last Answer(Abs)",
    };

    formatSummaryAccuracyTypesLabel= (accuracyTypeKey) => {
        let [answerType, accuracyType] = accuracyTypeKey.split("-");
        return [this.answerTypeIdLabelMap[answerType], this.accuracyTypeIdLabelMap[accuracyType]].join(" ");
    };

    formatSummaryChartLabel = (key) => {
        let [channel, answerType, accuracyType, snapshotIndex] = key.split("-");
        let name;
        if(answerType === "perUnitVariance") {
            name = `Net CP Per Unit Variance-${channel}`
        } else {
            name = `${this.answerTypeIdLabelMap[answerType]} ${this.accuracyTypeIdLabelMap[accuracyType]}-${channel}`
        }
        if(this.state.shownSummarySnapshots.length > 1) {
            let snapshot = this.props.snapshots[snapshotIndex];
            let snapshotName = snapshot.id === this.props.appConfig.liveSnapshotId ? "Live" : snapshot.name;
            return key.includes(GOAL_KEY) ? `${GOAL_KEY}-[${ snapshotName }]${ name }` : `[${ snapshotName }]${ name }`;
        }
        if (key.includes(GOAL_KEY)) {
            name = `${GOAL_KEY}-${this.answerTypeIdLabelMap[answerType]} ${this.accuracyTypeIdLabelMap[accuracyType]}-${channel}`
        }
        return name;
    };

    formatComponentChartLabel = (key) => {
        let [channel, accuracyType, component, snapshotIndex] = key.split("-");
        let map = this.getSortedKeySortedHeadingMap();
        if(this.state.shownComponentSnapshots.length > 1) {
            let snapshot = this.props.snapshots[snapshotIndex];
            let snapshotName = snapshot.id === this.props.appConfig.liveSnapshotId ? "Live" : snapshot.name;
            return key.includes(GOAL_KEY) ? `${GOAL_KEY}-[${snapshotName}]${map[accuracyType]}-${component}(${channel})` :
                   `[${snapshotName}]${map[accuracyType]}-${component}(${channel})`;
        }
        return key.includes(GOAL_KEY) ? `${GOAL_KEY}-${map[accuracyType]}-${component}(${channel})` : `${map[accuracyType]}-${component}(${channel})`;
    };

    getSummaryAccuracyDropdownOptions = () => {
        let accuracyTypeDropdownOptions = [];
        Object.keys(this.answerTypeIdLabelMap).map((answerType) => {
            Object.keys(this.accuracyTypeIdLabelMap).map((accuracyType) => {
                let accuracyTypeKey= [answerType, accuracyType].join("-");
                accuracyTypeDropdownOptions.push({'label':this.formatSummaryAccuracyTypesLabel(accuracyTypeKey), 'value': accuracyTypeKey});
            })
        });
        accuracyTypeDropdownOptions.unshift({'label':"Net CP Per Unit Variance(Contribution Profit)", 'value': "perUnitVariance- "});
        return accuracyTypeDropdownOptions;
    };

    updateSelectedOptions= (chosen, stateName) => {
        let options = chosen.detail.selectedOptions;
        let selectedKeys = [];
        for (let option of options) {
            selectedKeys.push(option.value);
        }
        this.setState({[stateName]: selectedKeys})
    };

    updateShownSummaryMetrics= (chosen) => {
        this.updateSelectedOptions(chosen, "shownSummaryMetrics");
    };

    updateShownSummaryChannel= (chosen) => {
        this.updateSelectedOptions(chosen, "shownSummaryChannels")
    };

    updateShownAccuracyVariance= (chosen) => {
        this.updateSelectedOptions(chosen, "shownComponentAccuracyKeys");
    };

    updateShownComponentChannel = (chosen) => {
        this.updateSelectedOptions(chosen, "shownComponentChannels");
    };

    updateShownComponents = (chosen) => {
        this.updateSelectedOptions(chosen, "shownComponents");
    };

    updateShownSummarySnapshots = (chosen) => {
        this.updateSelectedOptions(chosen, "shownSummarySnapshots")
    };

    updateShownComponentSnapshots = (chosen) => {
        this.updateSelectedOptions(chosen, "shownComponentSnapshots")
    };

    onToggleComponentChart = () => {
        let currState = this.state.showBarChartComponent;
        this.setState({ showBarChartComponent: !currState });
    };

    onToggleSummaryChart = () => {
        let currState = this.state.showBarChartSummary;
        this.setState({ showBarChartSummary: !currState });
    };

    onDownloadDisplayedSummaryData =() => {
        let displayedIds = this.getShownSummaryIds();
        let data = this.getDataForSharedMonths(this.processedSummaryData, this.state.shownSummarySnapshots);
        this.downloadDisplayedData(data, displayedIds, `Summary Trend Data_${new Date(Date.now()).toLocaleString()}.xls`, this.formatSummaryChartLabel)
    };

    onDownloadDisplayedComponentData = () => {
        let displayedIds = this.getShownComponentIds();
        let data = this.getDataForSharedMonths(this.processedComponentData, this.state.shownComponentSnapshots);
        this.downloadDisplayedData(data, displayedIds, `Component Trend Data_${new Date(Date.now()).toLocaleString()}.xls`, this.formatComponentChartLabel)
    };

    downloadDisplayedData = (data, displayedIds, filename, headingFormatter) => {
        this.setState({ reportDownloading: true});
        const headings = ["month/quarter", "formatted time"]
                         .concat(Object.keys(data[0]).filter(key => displayedIds.includes(key) && !["month", "formattedMonth"].includes(key))
                                                     .map(key => headingFormatter(key)));
        let array = [headings.join('\t')];
        data.forEach(row => {
            const rowItems = Object.keys(row).filter(key => displayedIds.includes(key) && !["month", "formattedMonth"].includes(key))
                .map(key => (row[key]));
            const rowArray = [row.month, row.formattedMonth].concat(rowItems);
            array.push(rowArray.join('\t'));
        });
        downloadFile(array.join('\n'), filename);
        this.setState({ reportDownloading: false});
    };

    getAccuracyVarianceDropdownOptions = () => {
        let map = this.getSortedKeySortedHeadingMap();
        let options = [];
        this.componentGraphAccuracyOptionKeys.map((key) =>
            options.push({'label':map[key], 'value':key}));
        return options;
    };

    getChannelDropdownOptions = () => {
        let options = [];
        this.allChannels.map((channel) => {
            options.push({'label':channel, 'value':channel})
        });
        return options;
    };

    getComponentDropdownOptions = () => {
        let options = [];
        this.allComponents.map((component) => {
            options.push({'label': component, 'value': component})
        });
        return options;
    };

    getSnapshotDropdownOptions = () => {
      let options = [];
      for(let snapshotIndex = 0; snapshotIndex < this.props.snapshots.length; snapshotIndex++) {
          let snapshot = this.props.snapshots[snapshotIndex];
          let name = snapshot.id === this.props.appConfig.liveSnapshotId? "Live" : snapshot.name;
          options.push({'label':name, 'value':snapshotIndex})
      }
      return options;
    };

    getShownSummaryIds = () => {
        let ids = [];
        this.state.shownSummaryChannels.map((channel) => {
            this.state.shownSummaryMetrics.map((accuracyType) => {
                this.state.shownSummarySnapshots.map((snapshotIndex) => {
                    ids.push([channel, accuracyType, snapshotIndex].join("-"));
                    if(accuracyType.includes('firstCpAvAccuracy')) {
                        ids.push([channel, accuracyType, snapshotIndex, GOAL_KEY].join("-"));
                    }
                })
            })
        });
        return ids;
    };

    getShownComponentIds = () => {
        let ids = [];
        this.state.shownComponentChannels.map((channel) => {
            this.state.shownComponentAccuracyKeys.map((accuracyType) => {
                this.state.shownComponents.map((component) => {
                    this.state.shownComponentSnapshots.map((snapshotIndex) => {
                        ids.push([channel, accuracyType, component, snapshotIndex].join("-"));
                        if(accuracyType.includes('dailyCPAccuracyAV')) {
                            ids.push([channel, accuracyType, component, snapshotIndex, GOAL_KEY].join("-"));
                        }
                    })
                })
            })
        });
        return ids;
    };

    bothAccuracyAndVarianceSelected = (keys) => {
      let includeAccuracy = false;
      let includeVariance = false;
      keys.map((item) => {
         if(item.includes("Accuracy")) {
             includeAccuracy = true;
         } else if(item.includes("Variance")) {
             includeVariance = true;
         }
      });
      return includeAccuracy && includeVariance;
    };

    getLegendType = (key) => {
      let channel = key.split("-")[0].toUpperCase();
      if(channel === CHANNELS.MFN) {
          return 'circle';
      } else if (channel === CHANNELS.RETAIL) {
          return 'square'
      } else if (channel === CHANNELS.FBA) {
          return 'diamond'
      } else if (channel === CHANNELS.WD) {
          return 'triangle'
      } else {
          return 'line'
      }
    };

    getYAxis=(bothAccuracyAndVarianceSelected, allLinesId) => {
        let labelValue = () => {
            if(bothAccuracyAndVarianceSelected) {
                return "%"
            }else {
                if(allLinesId.length === 0) {
                    return "";
                } else {
                    return allLinesId[0].includes("Accuracy")?"%":"$";
                }
            }
        };
        let yAxis = [];
        yAxis.push(<YAxis yAxisId="left"
                          label = {{ value: labelValue(), position: 'insideTopLeft'}}
                          type="number"
                          domain={['auto', labelValue() === "%"? 100:'auto']} key={1} />);
        if (bothAccuracyAndVarianceSelected) {
            yAxis.push(<YAxis yAxisId="right"
                              orientation="right"
                              label = {{ value: "$", position: 'insideTopRight'}}
                              type="number"
                              domain={['auto', 'auto']} key={2}/>)
        }
        return yAxis;
    };

    getLines=(allLinesId, bothAccuracyAndVarianceSelected, labelFormatter) => {
        let lines = [];
        let colorIndex = 0;
        {
            allLinesId.map((id) => {
                if (bothAccuracyAndVarianceSelected) {
                    if (id.includes(GOAL_KEY)) {
                        lines.push(<Line yAxisId="left"
                                         name={ labelFormatter(id) }
                                         strokeWidth='2'
                                         key={id}
                                         dataKey={id}
                                         stroke={ this.goalLineComponentColor }
                                         strokeDasharray="5 5"
                                         dot={<CustomizedDot/>}
                                         legendType={ this.getLegendType(id) } />);
                    } else if (id.includes("Accuracy")) {
                        this.goalLineComponentColor =  getMaximallyDifferentRandomColor(colorIndex++, allLinesId.length);
                        lines.push(<Line yAxisId="left"
                                         name={labelFormatter(id)}
                                         type="linear"
                                         strokeWidth='2'
                                         key={id}
                                         dataKey={id}
                                         stroke={ this.goalLineComponentColor }
                                         dot={<CustomizedDot/>}
                                         legendType={this.getLegendType(id)}/>)
                    } else {
                        lines.push(<Line yAxisId="right"
                                         name={labelFormatter(id)}
                                         type="linear"
                                         strokeWidth='2'
                                         key={id}
                                         dataKey={id}
                                         stroke={getMaximallyDifferentRandomColor(colorIndex++, allLinesId.length)}
                                         dot={<CustomizedDot/>}
                                         legendType={this.getLegendType(id)}/>)
                    }
                } else {
                    if (id.includes(GOAL_KEY)) {
                        lines.push(<Line yAxisId="left"
                                         name={ labelFormatter(id) }
                                         strokeWidth='2'
                                         key={id}
                                         dataKey={id}
                                         stroke={this.goalLineSummaryColor}
                                         strokeDasharray="5 5"
                                         dot={<CustomizedDot/>}
                                         legendType={ this.getLegendType(id) } />);
                    } else {
                        this.goalLineSummaryColor =  getMaximallyDifferentRandomColor(colorIndex++, allLinesId.length);
                        lines.push(<Line yAxisId="left"
                                         name={ labelFormatter(id) }
                                         type="linear"
                                         strokeWidth='2'
                                         key={id}
                                         dataKey={id}
                                         stroke={ this.goalLineSummaryColor }
                                         dot={ <CustomizedDot /> }
                                         legendType={ this.getLegendType(id) } />);
                    }
                }
            })
        }
        return lines;
    };

    getBars=(allLinesId, bothAccuracyAndVarianceSelected, labelFormatter) => {
        let bars = [];
        let colorIndex = 0;
        {
            allLinesId.map((id) => {
                if (bothAccuracyAndVarianceSelected) {
                    if (id.includes(GOAL_KEY)) {
                        bars.push(<Line yAxisId="left"
                                        name={ labelFormatter(id) }
                                        strokeWidth='2'
                                        key={id}
                                        dataKey={id}
                                        stroke={ this.goalLineComponentColor }
                                        strokeDasharray="5 5"
                                        legendType={ this.getLegendType(id) } />);
                    } else if (id.includes("Accuracy")) {
                        bars.push(<Bar yAxisId="left"
                                       name={labelFormatter(id)}
                                       key={id}
                                       dataKey={id}
                                       fill={getMaximallyDifferentRandomColor(colorIndex++, allLinesId.length)}
                                       legendType='square'/>)
                    } else {
                        bars.push(<Bar yAxisId="right"
                                       name={labelFormatter(id)}
                                       key={id}
                                       dataKey={id}
                                       fill={getMaximallyDifferentRandomColor(colorIndex++, allLinesId.length)}
                                       legendType='square'/>)
                    }
                } else {
                    if (id.includes(GOAL_KEY)) {
                        bars.push(<Line yAxisId="left"
                                        name={ labelFormatter(id) }
                                        strokeWidth='2'
                                        key={id}
                                        dataKey={id}
                                        stroke={ this.goalLineSummaryColor }
                                        strokeDasharray="5 5"
                                        legendType={this.getLegendType(id)} />);
                    } else {
                        bars.push(<Bar yAxisId="left"
                                       name={ labelFormatter(id) }
                                       key={id}
                                       dataKey={id}
                                       fill={ getMaximallyDifferentRandomColor(colorIndex++, allLinesId.length) }
                                       legendType='square' />)
                    }
                }
            })
        }
        return bars;
    };

    getLineChart=(data, bothAccuracyAndVarianceSelected, allLinesId, labelFormatter) => {
        return (
            <LineChart
                data={data}
                margin={{top: 10, right: 30, left: 20, bottom: 10,}}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedMonth" />
                {this.getYAxis(bothAccuracyAndVarianceSelected, allLinesId)}
                <Tooltip />
                <Legend />
                {this.getLines(allLinesId, bothAccuracyAndVarianceSelected, labelFormatter)}
            </LineChart>
        )
    };

    getBarChart=(data, bothAccuracyAndVarianceSelected, allLinesId, labelFormatter) => {
     return (
         <ComposedChart
             data={data}
             margin={{top: 10, right: 30, left: 20, bottom: 10,}}
         >
             <CartesianGrid strokeDasharray="3 3" />
             <XAxis dataKey="formattedMonth" />
             {this.getYAxis(bothAccuracyAndVarianceSelected, allLinesId)}
             <Tooltip />
             <Legend />
             {this.getBars(allLinesId, bothAccuracyAndVarianceSelected, labelFormatter)}
         </ComposedChart>
     )
    };

    getDataForSharedMonths = (data, shownSnapshots) => {
        if(data.length === 0 || shownSnapshots.length === 0) {
            return data;
        }
        let sortedMonths = shownSnapshots.map(snapshotIndex => {
            return this.loadedAccuracyData[snapshotIndex].sortedMonths;
        });
        let sharedMonth = sortedMonths.shift().filter(month => {
            return sortedMonths.every(sortedMonth => {
                return sortedMonth.indexOf(month) !== -1;
            })
        });
        return sharedMonth.map(month => {
            return data[month]
        });

    };

    getChartTitle=(text, allLinesId, checked, onChangeFn) => {
        let data = this.getDataForSharedMonths(this.processedComponentData, this.state.shownComponentSnapshots);
        return (
            <div className="awsui-util-container-header">
                <div className="awsui-util-action-stripe">
                    <div className="awsui-util-action-stripe-group">
                        <div className="awsui-util-font-size-2">{text}</div>
                    </div>
                    <div className="awsui-util-f-r">
                        { text.includes('Summary') && (
                        <Button
                          text="Download Graph"
                          variant="normal"
                          icon="download"
                          disabled={allLinesId.length === 0 || data.length === 0}
                          loading={this.state.reportDownloading}
                          onClick={this.onDownloadDisplayedSummaryData}
                        />
                        ) }
                        { text.includes('Component') && (
                          <Button
                            text="Download Graph"
                            variant="normal"
                            icon="download"
                            disabled={allLinesId.length === 0 || data.length === 0}
                            loading={this.state.reportDownloading}
                            onClick={this.onDownloadDisplayedComponentData}
                          />
                        ) }
                        { checked && (
                        <Button
                            text="Switch to Line Chart"
                            variant="normal"
                            disabled={allLinesId.length === 0}
                            onClick={onChangeFn}
                        /> )
                         || (
                          <Button
                            text="Switch to Bar Chart"
                            variant="normal"
                            disabled={allLinesId.length === 0}
                            onClick={onChangeFn}
                          />
                        ) }
                    </div>
                </div>
            </div>
        );
    };

    getSummaryTrendChart = () => {
        let bothAccuracyAndVarianceSelected = this.bothAccuracyAndVarianceSelected(this.state.shownSummaryMetrics);
        let allLinesId = this.getShownSummaryIds();
        let data = this.getDataForSharedMonths(this.processedSummaryData, this.state.shownSummarySnapshots);
        return (
            <div key={1} className="awsui-util-container">
                {this.getChartTitle("Summary Accuracy Trend", allLinesId, this.state.showBarChartSummary, this.onToggleSummaryChart)}
                <ColumnLayout columns={3}>
                    <div data-awsui-column-layout-root="true">
                        <div>
                            <TrendFilter
                                text="Channel"
                                classname={AccuracyButtonNewStyle}
                                options={this.getChannelDropdownOptions()}
                                onChange={this.updateShownSummaryChannel}
                                selectedIds={this.state.shownSummaryChannels}
                            />
                        </div>
                        <div>
                            <TrendFilter
                                text="Accuracy/Per Unit Variance"
                                classname='AccuracyTypeMultiSelect'
                                options={this.getSummaryAccuracyDropdownOptions()}
                                onChange={this.updateShownSummaryMetrics}
                                selectedIds={this.state.shownSummaryMetrics}
                            />
                        </div>
                        <div>
                            <TrendFilter
                                text="Snapshots"
                                classname={AccuracyButtonNewStyle}
                                options={this.getSnapshotDropdownOptions()}
                                onChange={this.updateShownSummarySnapshots}
                                selectedIds={this.state.shownSummarySnapshots}
                            />
                        </div>
                    </div>
                </ColumnLayout>
                <div style={{width:'99%', padding:'0'}}>
                <ResponsiveContainer aspect={3}>
                    {this.state.showBarChartSummary && (
                        this.getBarChart(data, bothAccuracyAndVarianceSelected, allLinesId, this.formatSummaryChartLabel)) ||
                    (!this.state.showBarChartSummary && (
                        this.getLineChart(data, bothAccuracyAndVarianceSelected, allLinesId, this.formatSummaryChartLabel)))}
                </ResponsiveContainer>
                </div>
            </div>
        );
    };

    getComponentTrendChart = () => {
        let bothAccuracyAndVarianceSelected = this.bothAccuracyAndVarianceSelected(this.state.shownComponentAccuracyKeys);
        let allLinesId = this.getShownComponentIds();
        let data = this.getDataForSharedMonths(this.processedComponentData, this.state.shownComponentSnapshots);
        return (
            <div key={2} className="awsui-util-container">
                {this.getChartTitle("Components Accuracy/Variance Trend", allLinesId, this.state.showBarChartComponent, this.onToggleComponentChart)}
                <ColumnLayout columns={4}>
                    <div data-awsui-column-layout-root="true">
                        <div>
                            <TrendFilter
                                text="Channel"
                                classname={AccuracyButtonNewStyle}
                                options={this.getChannelDropdownOptions()}
                                onChange={this.updateShownComponentChannel}
                                selectedIds={this.state.shownComponentChannels}
                            />
                        </div>
                        <div>
                            <TrendFilter
                                text="Accuracy/Per Unit Variance"
                                classname={AccuracyButtonNewStyle}
                                options={this.getAccuracyVarianceDropdownOptions()}
                                onChange={this.updateShownAccuracyVariance}
                                selectedIds={this.state.shownComponentAccuracyKeys}
                            />
                        </div>
                        <div>
                            <TrendFilter
                                text="Component"
                                classname={AccuracyButtonNewStyle}
                                options={this.getComponentDropdownOptions()}
                                onChange={this.updateShownComponents}
                                selectedIds={this.state.shownComponents}
                            />
                        </div>
                        <div>
                            <TrendFilter
                                text="Snapshots"
                                classname={AccuracyButtonNewStyle}
                                options={this.getSnapshotDropdownOptions()}
                                onChange={this.updateShownComponentSnapshots}
                                selectedIds={this.state.shownComponentSnapshots}
                            />
                        </div>
                    </div>
                </ColumnLayout>
                <div style={{width:'99%', padding:'0'}}>
                <ResponsiveContainer aspect={3}>
                    {this.state.showBarChartComponent && (
                        this.getBarChart(data, bothAccuracyAndVarianceSelected, allLinesId, this.formatComponentChartLabel)) ||
                    (!this.state.showBarChartComponent && (
                        this.getLineChart(data, bothAccuracyAndVarianceSelected, allLinesId, this.formatComponentChartLabel)))}
                </ResponsiveContainer>
                </div>
            </div>
        )
    };

    getTrendCharts = () => {
        let charts = [];
        charts.push(this.getSummaryTrendChart());
        charts.push(this.getComponentTrendChart());
        return charts;
    };

    render() {
        return (
            <div>
                {this.state.emptyRecords && (
                    <div className="awsui-util-t-c">
                        <div className="awsui-util-pt-xs awsui-util-mb-xs">
                            <b>This table was empty or could not be found.</b>
                        </div>
                    </div>
                )}
                {!this.state.emptyRecords && this.state.loadDone && !this.state.loadError && (
                    <div>
                        {this.getTrendCharts()}
                    </div>
                )}
                {!this.state.emptyRecords && this.state.loadDone && this.state.loadError && (
                    <Flash dismissible={false} type="error">
                        {this.state.errorState} fetching the data, retry the request. Contact us if the error persists. PI Tools dev team has been
                        notified via ticket:{' '}
                        <a href={'https://tiny.amazon.com/11ia80cg5'} target="_blank">
                            Ticket Queue
                        </a>
                    </Flash>
                )}
                {!this.state.loadDone && (
                    <div>
                        <Spinner/>
                        <div className="awsui-util-d-ib">Loading Data...</div>
                    </div>
                )}
                {!this.state.loadDone && this.totalMonths > 6 && (
                    <div style={{marginTop: '1em'}}>
                        <Alert>
                            Total load time might take longer than 15 seconds.
                        </Alert>
                    </div>
                )}
            </div>
        );
    };
}




// WEBPACK FOOTER //
// ./src/components/content/accuracy_trend.jsx